
import { SiteUrls } from '@tableau/shared-urls';
import { CatalogUrlParamKeys } from '@tableau/shared-urls/lib/src/SiteUrls';
import React from 'react';
import { Messages } from '../../../../../gen-src/Localize';
import { ColumnFieldsQueryVariables, ColumnFieldsQuery, ColumnFieldsNodesFragment , ColumnFieldsDocument } from '../../../../../gen-src/queries';
import { ConnectionListResult, PaginatedQueryWrapper } from '../../../data-catalog-shared';
import { LinksDisplayGrid, LinksDisplayGridRowData } from '../sharedDisplayGrid/LinksDisplayGrid';

interface ColumnFieldsGridRowData extends LinksDisplayGridRowData {
  readonly publishedDatasourceVizportalUrlId: string;
  readonly embeddedDatasourceNodeId: string;
  readonly datasourceName: string;
  readonly workbookVizportalUrlId: string;
}

type QueryVariables = ColumnFieldsQueryVariables;
type QueryResults = ColumnFieldsQuery;
type QueryResultListNode = ColumnFieldsNodesFragment;
type GridRowData = ColumnFieldsGridRowData;

function selectConnectionListResult(resultData: ColumnFieldsQuery): ConnectionListResult<QueryResultListNode> | null {
  return resultData && resultData.columns && resultData.columns[0] && resultData.columns[0].referencedByFieldsConnection;
}

function mapResultNode({ id, name, ...node }: QueryResultListNode): GridRowData {
  const workbookVizportalUrlId = node.datasource?.__typename === 'EmbeddedDatasource' && node.datasource.workbook ? node.datasource.workbook.vizportalUrlId : '';
  const embeddedDatasourceNodeId = node.datasource?.__typename === 'EmbeddedDatasource' ? node.datasource.id : '';
  const publishedDatasourceVizportalUrlId = node.datasource?.__typename === 'PublishedDatasource' ? node.datasource.vizportalUrlId : '';

  return {
    id,
    name,
    workbookVizportalUrlId,
    embeddedDatasourceNodeId,
    publishedDatasourceVizportalUrlId,
    datasourceName: node.datasource?.name ?? '',
  };
}

function urlForLink(urls: SiteUrls, rowData: ColumnFieldsGridRowData) {
  return rowData.workbookVizportalUrlId ?
    urls.workbookLineageFieldPath(rowData.workbookVizportalUrlId, rowData.id) +
    filterToEmbeddedDatasource(rowData.embeddedDatasourceNodeId) :
    urls.datasourceLineageFieldPath(rowData.publishedDatasourceVizportalUrlId, rowData.id);
}

function filterToEmbeddedDatasource(datasourceId: string): string {
  return `?${CatalogUrlParamKeys.embeddedDataSource}=` + datasourceId;
}

function nameForLink(rowData: ColumnFieldsGridRowData): string | null {
  if (rowData.name === null) {
    return null;
  }

  return Messages.LinkNameWithParent({ name: rowData.name, parentName: rowData.datasourceName });
}

export type ColumnFieldsQueryGridProps = {
  readonly columnId: string;
  readonly rowCount: number | null;
};

export function ColumnFieldsQueryGrid(props: ColumnFieldsQueryGridProps): JSX.Element {
  const emptyGridMessage = Messages.EmptyColumnUsedByFieldsGridMessage();
  return (
    <PaginatedQueryWrapper<QueryVariables, QueryResults, QueryResultListNode, GridRowData>
      queryGql={ColumnFieldsDocument}
      partialQueryVariables={{ columnId: props.columnId }}
      selectConnectionListResult={selectConnectionListResult}
      mapResultNode={mapResultNode}
    >
      {({ rowStore }) =>
        <LinksDisplayGrid
          rowStore={rowStore}
          emptyGridMessage={emptyGridMessage}
          rowCount={props.rowCount}
          link={urlForLink}
          name={nameForLink}
        />
      }
    </PaginatedQueryWrapper>
  );
}
